import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import UserTopbar from "../UserTopbar";
import ApiUri from "../../Helpers/apiUrl";
import {
  Box,
  LinearProgress,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Grid,
  Paper,
  Stack,
  Chip,
  Fade,
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff,
  CheckCircle,
  Error
} from "@mui/icons-material";

import { handleApiError } from "../../Helpers/handleApiError ";
import { useNavigate } from "react-router-dom";

function ChangePassword() {
  const  navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [password_confirmation, setConfirmed] = useState("");
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordStatus, setPasswordStatus] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
    match: false,
  });
  const [showStatusBox, setShowStatusBox] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const validatePassword = useCallback(() => {
    const status = {
      length: password.length >= 14,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      match: password === password_confirmation && password.length > 0,
    };
    setPasswordStatus(status);
    const isValid = Object.values(status).every(val => val) && oldPassword.length > 0;
    setIsFormValid(isValid);
    return isValid;
  }, [password, password_confirmation, oldPassword]);

  useEffect(() => {
    validatePassword();
  }, [validatePassword]);

  const handleClickShowPassword = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      Swal.fire({
        icon: "error",
        title: "Password Requirements Not Met",
        html: `
          <div style="text-align: left;">
            <p>Your password must contain:</p>
            <ul>
              <li>At least 14 characters</li>
              <li>Uppercase letter</li>
              <li>Lowercase letter</li>
              <li>Number</li>
              <li>Special character</li>
              <li>Matching confirmation</li>
            </ul>
          </div>
        `,
      });
      return;
    }

    try {
      const user = localStorage.getItem("u_token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + user,
      };
      const data = {
        oldPassword: oldPassword,
        password: password,
        password_confirmation: password_confirmation,
      };
      const response = await axios.post(`${ApiUri}change-password`, data, {
        headers,
      });
      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          timer: 3000,
        });
        localStorage.removeItem('user');
        
        navigate('/');
      }
      else{
        Swal.fire({
          icon: "error",
          title: "error",
          text: response.data.error,
          timer: 3000,
        });
      }
    } catch (error) {
      handleApiError(error, "Failed to update password. Please try again.");
    }
  };

  const passwordStrength = () => {
    const requirementsMet = Object.values(passwordStatus)
      .slice(0, 5) // Only count the first 5 requirements (exclude match)
      .filter(val => val).length;
    return (requirementsMet / 5) * 100;
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      backgroundColor: "#f5f7fa",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    }}>
      {/* Background Shapes for Glassmorphism */}
      <Box sx={{
        position: 'absolute',
        top: '-5%',
        left: '10%',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
        filter: 'blur(80px)',
        zIndex: 0,
        opacity: 0.6,
        animation: 'float 10s ease-in-out infinite'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '5%',
        right: '10%',
        width: '450px',
        height: '450px',
        borderRadius: '50%',
        background: 'linear-gradient(45deg, #9c27b0, #ce93d8)',
        filter: 'blur(90px)',
        zIndex: 0,
        opacity: 0.5,
      }} />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <UserTopbar />
      </Box>

      <Box sx={{ flexGrow: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", pt: "5%", px: 2, pb: 4, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "500px",
            padding: "2.5rem",
            borderRadius: "24px",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.2)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            position: "relative",
          }}
        >
        <Typography 
          variant="h5" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ 
            fontWeight: 600,
            color: "primary.main",
            mb: 3
          }}
        >
          Change Password
        </Typography>
        
        <Box 
          component="form" 
          onSubmit={updatePassword} 
          sx={{ mt: 3 }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type={showPassword.oldPassword ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                // sx={{
                //   "& .MuiOutlinedInput-root": {
                //     borderRadius: "",
                //   }
                // }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleClickShowPassword("oldPassword")}
                        edge="end"
                        sx={{ color: "text.secondary" }}
                      >
                        {showPassword.oldPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword.newPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setShowStatusBox(true)}
                onBlur={() => setShowStatusBox(false)}
                required
                // sx={{
                //   "& .MuiOutlinedInput-root": {
                //     borderRadius: "12px",
                //   }
                // }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleClickShowPassword("newPassword")}
                        edge="end"
                        sx={{ color: "text.secondary" }}
                      >
                        {showPassword.newPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Fade in={showStatusBox || password.length > 0}>
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    right: { xs: 0, sm: -280 },
                    top: 180,
                    width: 250,
                    p: 2,
                    borderRadius: "12px",
                    backgroundColor: "background.paper",
                    zIndex: 1,
                    display: { xs: "none", sm: "block" }
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Password Requirements
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {passwordStatus.length ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Error color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Minimum 14 characters
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {passwordStatus.uppercase ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Error color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Uppercase letter
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {passwordStatus.lowercase ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Error color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Lowercase letter
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {passwordStatus.digit ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Error color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Number
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {passwordStatus.specialChar ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Error color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Special character
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Fade>
              
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength()}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                  }}
                  color={
                    passwordStrength() < 50
                      ? "error"
                      : passwordStrength() < 80
                      ? "warning"
                      : "success"
                  }
                />
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between",
                  mt: 0.5
                }}>
                  <Typography variant="caption">
                    Password strength
                  </Typography>
                  <Chip 
                    label={
                      passwordStrength() < 50 ? "Weak" :
                      passwordStrength() < 80 ? "Good" : "Strong"
                    }
                    size="small"
                    color={
                      passwordStrength() < 50 ? "error" :
                      passwordStrength() < 80 ? "warning" : "success"
                    }
                  />
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPassword.confirmPassword ? "text" : "password"}
                value={password_confirmation}
                onChange={(e) => setConfirmed(e.target.value)}
                required
                error={password_confirmation.length > 0 && !passwordStatus.match}
                helperText={
                  password_confirmation.length > 0 && !passwordStatus.match
                    ? "Passwords do not match"
                    : passwordStatus.match 
                    ? "Passwords match" 
                    : ""
                }
                // sx={{
                //   "& .MuiOutlinedInput-root": {
                //     borderRadius: "12px",
                //   }
                // }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleClickShowPassword("confirmPassword")}
                        edge="end"
                        sx={{ color: "text.secondary" }}
                      >
                        {showPassword.confirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ 
              mt: 4,
              py: 1.5,
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              }
            }}
            disabled={!isFormValid}
          >
            Update Password
          </Button>
        </Box>
      </Box>
    </Box>
    </Box>
  );
}

export default ChangePassword;